import { useEffect, useRef, useState } from "react";
import { X, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { subscribeOptionsChanged, notifyOptionsChanged } from "@/lib/optionsBus";

/**
 * MultiTagInput — typing-friendly multi-select with create-new.
 * Uses a native input + absolutely-positioned dropdown (no Popover) to avoid focus issues.
 */
export default function MultiTagInput({ values = [], onChange, optionType, placeholder = "Add…", testId }) {
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const mounted = useRef(true);

  const fetchOptions = async () => {
    try {
      const { data } = await api.get(`/options/${optionType}`);
      if (mounted.current) setOptions(data || []);
    } catch (e) { console.error(`Failed to fetch ${optionType} options:`, e); }
  };

  useEffect(() => {
    mounted.current = true;
    fetchOptions();
    const unsub = subscribeOptionsChanged(optionType, () => fetchOptions());
    return () => { mounted.current = false; unsub(); };
    // eslint-disable-next-line
  }, [optionType]);

  useEffect(() => {
    function onDoc(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const add = async (val) => {
    const v = (val || "").trim();
    if (!v) return;
    if (!values.includes(v)) {
      onChange([...values, v]);
      if (!options.includes(v)) {
        try { await api.post(`/options/${optionType}`, { value: v }); } catch (e) { console.error(`Failed to save ${optionType} option:`, e); }
        setOptions((p) => (p.includes(v) ? p : [...p, v].sort()));
        notifyOptionsChanged(optionType);
      }
    }
    setSearch("");
    inputRef.current?.focus();
  };

  const remove = (v) => onChange(values.filter((x) => x !== v));

  const filtered = options.filter(
    (o) => !values.includes(o) && o.toLowerCase().includes(search.toLowerCase())
  );
  const showCreate = search.trim() && !options.some((o) => o.toLowerCase() === search.trim().toLowerCase());

  return (
    <div ref={wrapperRef} className="relative">
      <div
        className="min-h-10 w-full border border-input rounded-md px-2 py-1.5 flex flex-wrap items-center gap-1.5 bg-white focus-within:ring-2 focus-within:ring-[#0055FF]/20 focus-within:border-[#0055FF]"
        onClick={() => inputRef.current?.focus()}
        data-testid={testId}
      >
        {values.map((v) => (
          <Badge key={v} variant="secondary" className="gap-1 pl-2 pr-1 py-0.5 bg-slate-100 text-slate-800 hover:bg-slate-200">
            {v}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); remove(v); }}
              className="ml-0.5 rounded hover:bg-slate-300 p-0.5"
              data-testid={`${testId}-remove-${v}`}
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); add(search); }
            else if (e.key === "Backspace" && !search && values.length) remove(values[values.length - 1]);
          }}
          placeholder={values.length ? "" : placeholder}
          className="flex-1 min-w-[140px] h-7 border-0 outline-none bg-transparent text-sm placeholder:text-[#94a3b8]"
          data-testid={`${testId}-input`}
        />
      </div>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-md shadow-lg max-h-60 overflow-auto">
          {showCreate && (
            <button
              type="button"
              onClick={() => add(search)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 text-[#0055FF]"
              data-testid={`${testId}-create`}
            >
              <Plus className="w-4 h-4" /> Add "{search}"
            </button>
          )}
          {filtered.map((opt) => (
            <button
              type="button"
              key={opt}
              onClick={() => add(opt)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
            >
              {opt}
            </button>
          ))}
          {filtered.length === 0 && !showCreate && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {search.trim() ? "Already added" : "Type to search or add new"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
